
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { Mutation, State } from 'vuex-class';
import AuthActions from '~/mixins/AuthActions';
import { ThemeName } from '~/store/types';
import {
  ISanityDocuments,
  SanityDocumentSchema,
} from '~/types/sanity-documents';
import isQualified from '~/queries/isQualified.gql';
import SanityRowWithColumns from '~/components/Sanity/SanityRowWithColumns.vue';
import SanityRowTextWithImage from '~/components/Sanity/SanityRowTextWithImage.vue';

@Component({
  components: {
    SanityRowTextWithImage,
    SanityRowWithColumns,
  },
  meta: {
    theme: ThemeName.DARK,
  },
})
export default class CustomPage extends Mixins(AuthActions) {
  @Prop(Boolean) readonly loading!: boolean;
  @Prop(Object)
  readonly doc!: ISanityDocuments[SanityDocumentSchema.CUSTOM_PAGE];

  @Mutation toggleErrorSnackbar!: (payload?: boolean) => void;
  @Mutation updateSnackbarErrorText!: (args: any) => void;

  @State((profile) => profile.user.loggedIn, { namespace: 'profile' })
  loggedIn!: boolean;

  defaultTheme = ThemeName.DARK;

  @Watch('doc.requiresLogin', { immediate: true })
  handleRequiresLoginChange(current: boolean | undefined) {
    if (current && !this.loggedIn) {
      this.goToLoginService();
      return;
    }
  }

  @Watch('doc.restrictionId', { immediate: true })
  async handleRestrictionIdChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current && current !== previous) {
      const isQualified = await this.isQualifiedCheck(current);
      if (!isQualified) {
        this.toggleErrorSnackbar(true);
        this.updateSnackbarErrorText(this.$t('forms.isNotQualifiedError'));
        return this.goTo(this.doc?.redirectIfNotQualifiedUrl || '');
      }
    }
  }

  goTo(route: string) {
    if (route.startsWith('/')) return this.$router.push(route);
    window.location.href = route;
  }

  goToLoginService() {
    const redirect = this.$sanityLookup.getPageRoutePath({
      slug: this.$route.params?.slug,
      schema: SanityDocumentSchema.CUSTOM_PAGE,
    });
    return this.promptToLogin({
      redirectAfterLoginPath: redirect || '/',
    });
  }

  async isQualifiedCheck(restrictionId: string) {
    const payload = {
      sanityDocumentSlug: this.doc.slug,
      sanityDocumentId: this.doc._id,
      sanityDocumentType: SanityDocumentSchema.CUSTOM_PAGE,
    };

    const { data } = await this.$apollo.query({
      query: isQualified,
      variables: {
        id: restrictionId,
        payload,
      },
      fetchPolicy: 'network-only',
    });

    const uaPayload = {
      payload,
      restrictionId,
      restrictionResults: data?.isQualified,
    };

    if (data?.isQualified?.success === true) {
      //UA tracking
      this.$ua.trackFormRestrictionSatisfiedEvent(uaPayload);
      return true;
    }

    this.$ua.trackFormRestrictionNotSatisfiedEvent(uaPayload);
    return false;
  }
}
